// @ts-strict-ignore
import * as React from 'react';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Result from 'antd/lib/result';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import { MehOutlined } from '@ant-design/icons';
import {
    Integration,
    LifecycleStep,
    ReleaseStatus,
    ReleaseTimelinePhase,
    ReleaseType,
    WorkflowEnabledSteps,
} from '@/types';
import { Destination } from '@/types/destinations';
import {
    OverviewDestinationStatus,
    WorkflowOverview as WorkflowOverviewData,
    WorkflowRunOverviewDisplayStatus,
} from '@/types/workflowOverview';
import { WorkflowRunOverviewCard } from './WorkflowRunOverviewCard';
import { WorkflowRunStatusWidgetsContainer } from './WorkflowRunStatusWidgetsContainer';
import { WorkflowRunDestinationBuildsWidget } from './widgets/WorkflowRunDestinationBuildsWidget';
import { ReleaseDescriptionWidget } from '@/components/appHome/widgets/ReleaseDescriptionWidget';
import { ReleaseMetricsWidget } from '@/components/appHome/widgets/ReleaseMetricsWidget';
import { ReleaseTimelineWidget } from '@/components/appHome/widgets/ReleaseTimelineWidget';
import { WorkflowRunRolloutWidgetsContainer } from './widgets/WorkflowRunRolloutWidgetsContainer';
import { WorkflowRunDatesWidgetProps } from './widgets/WorkflowRunDatesWidget';
import { FeatureCompletionWidget } from '@/components/appHome/widgets/FeatureCompletionWidget';
import { WorkflowRunStepsGraphWidget } from './widgets/WorkflowRunStepsGraphWidget';
import type { WorkflowRunStepsGraphWidgetProps } from './widgets/WorkflowRunStepsGraphWidget';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { colorForWorkflowRunOverviewDisplayStatus } from '@/helpers/displayHelpers';
import { antGrey, colorWithAlpha, runwayColors } from '@/components/styles/globalStyles';
import Flex from 'antd/lib/flex';
import { Empty } from 'antd/lib';

export interface WorkflowOverviewProps {
    workflowOverview?: WorkflowOverviewData | null;
    isLoading: boolean;
    shouldDisplayFetchFailed: boolean;
    onRetry: () => void;
    orgId: string;
    appId: string;
    orgIdParam: string;
    appIdParam: string;
    selectedDestinationIds: string[];
    resolvedDestinations: Destination[];
    runCardDestinationDisplayText: string | null;
    isWorkflowsEnabledAndMultiDestination: boolean;
    stabilityIntegrations: Integration[];
    workflowEnabledSteps: WorkflowEnabledSteps;
    workflowEnabledLifecycleSteps: LifecycleStep[];
    shouldHideNonBlockingSteps: boolean;
    nonBlockingStepsConfigMap: WorkflowRunStepsGraphWidgetProps['nonBlockingStepsConfigMap'];
}

export const WorkflowOverview: React.FC<WorkflowOverviewProps> = ({
    workflowOverview,
    isLoading,
    shouldDisplayFetchFailed,
    onRetry,
    orgId,
    appId,
    orgIdParam,
    appIdParam,
    selectedDestinationIds,
    resolvedDestinations,
    runCardDestinationDisplayText,
    isWorkflowsEnabledAndMultiDestination,
    stabilityIntegrations,
    workflowEnabledSteps,
    workflowEnabledLifecycleSteps,
    shouldHideNonBlockingSteps,
    nonBlockingStepsConfigMap,
}) => {
    const shouldShowOverviewContent =
        !!workflowOverview && !!workflowOverview.workflowRuns && !isLoading && !shouldDisplayFetchFailed;

    return (
        <>
            {shouldShowOverviewContent &&
                workflowOverview.workflowRuns.map((singleRunOverview, runIndex) => {
                    const isUpcomingRun = singleRunOverview.timelinePhase === ReleaseTimelinePhase.Upcoming;
                    const isCurrentRun = singleRunOverview.timelinePhase === ReleaseTimelinePhase.Current;
                    const isPastRun = singleRunOverview.timelinePhase === ReleaseTimelinePhase.Completed;
                    const isSkippedRun = singleRunOverview.status === ReleaseStatus.Skipped;

                    const haveAnyEvents = !!singleRunOverview.events && singleRunOverview.events.length > 0;
                    const haveAnyStats = !!singleRunOverview.stats && singleRunOverview.stats.length > 0;

                    const shouldShowTimelineWidget = (isCurrentRun || isUpcomingRun) && haveAnyEvents;
                    const shouldShowStatsWidget = (isPastRun || isCurrentRun) && !isSkippedRun && haveAnyStats;

                    const hasRollingOutDisplayStatus =
                        singleRunOverview.overviewDisplayStatus === WorkflowRunOverviewDisplayStatus.RollingOut;
                    const hasCompletedDisplayStatus =
                        singleRunOverview.overviewDisplayStatus === WorkflowRunOverviewDisplayStatus.Completed;

                    const shouldShowRolloutsWidgets = hasRollingOutDisplayStatus || hasCompletedDisplayStatus;

                    const hasNewerRollingOutRun = workflowOverview.workflowRuns
                        .slice(0, runIndex)
                        .some((run) => run.overviewDisplayStatus === WorkflowRunOverviewDisplayStatus.RollingOut);

                    const hasHaltedRollout =
                        hasRollingOutDisplayStatus &&
                        selectedDestinationIds.some(
                            (destinationStepId) =>
                                singleRunOverview.destinationStatusByDestinationStepId?.[destinationStepId] ===
                                OverviewDestinationStatus.RolloutHalted,
                        );

                    const headerTintColor = (() => {
                        if (singleRunOverview.overviewDisplayStatus === WorkflowRunOverviewDisplayStatus.Skipped) {
                            return null;
                        }
                        if (hasHaltedRollout) {
                            return runwayColors.statusRed;
                        }
                        if (hasCompletedDisplayStatus) {
                            return hasNewerRollingOutRun ? null : runwayColors.statusGreen;
                        }
                        return colorForWorkflowRunOverviewDisplayStatus(singleRunOverview.overviewDisplayStatus);
                    })();

                    const selectedDestinations = resolvedDestinations.filter((dest) =>
                        selectedDestinationIds.includes(dest.stepConfigId),
                    );
                    const destinationCounterSize = selectedDestinationIds.length !== 1 ? 20 : 24;

                    return (
                        <WorkflowRunOverviewCard
                            key={singleRunOverview.workflowRunId}
                            orgId={orgId}
                            workflowId={singleRunOverview.workflowId}
                            workflowRunId={singleRunOverview.workflowRunId}
                            releaseId={singleRunOverview.releaseId}
                            displayableVersion={singleRunOverview.displayableVersion}
                            headerComponent={
                                <WorkflowRunStatusWidgetsContainer
                                    overviewDisplayStatus={singleRunOverview.overviewDisplayStatus}
                                    allDestinations={resolvedDestinations}
                                    selectedDestinationStepIds={selectedDestinationIds}
                                    statusesByDestinationStepId={singleRunOverview.destinationStatusByDestinationStepId}
                                    phasedInfoByDestinationStepId={
                                        singleRunOverview.phasedReleaseInfosByDestinationStepId
                                    }
                                    reviewStartedDateByDestinationStepId={
                                        singleRunOverview.startedReviewAtByDestinationStepId
                                    }
                                    legacyReviewStartedDate={singleRunOverview.startedReviewAt}
                                    isHotfix={singleRunOverview.type === ReleaseType.Hotfix}
                                    isRollback={singleRunOverview.type === ReleaseType.Rollback}
                                    hasNewerRollingOutRun={hasNewerRollingOutRun}
                                />
                            }
                            contentTopline={
                                <Flex align="center" justify="flex-start" flex={'1'} gap={17}>
                                    {isWorkflowsEnabledAndMultiDestination && (
                                        <>
                                            <DestinationCounter
                                                destinations={selectedDestinations}
                                                size={destinationCounterSize}
                                            />
                                            <div
                                                style={{
                                                    fontWeight: 300,
                                                    fontSize: 16,
                                                    color: antGrey.grey7,
                                                }}
                                            >
                                                {runCardDestinationDisplayText}
                                            </div>
                                        </>
                                    )}
                                    <WorkflowRunDestinationBuildsWidget
                                        allDestinations={resolvedDestinations}
                                        selectedDestinationStepIds={selectedDestinationIds}
                                        appStoreBuildsByDestinationStepId={
                                            singleRunOverview.selectedAppStoreBuildsByDestinationStepId
                                        }
                                    />
                                </Flex>
                            }
                            contentComponent={
                                <>
                                    <ReleaseDescriptionWidget description={singleRunOverview.workflowPlanningSummary} />
                                    {shouldShowStatsWidget && (
                                        <ReleaseMetricsWidget
                                            orgId={orgIdParam}
                                            appId={appIdParam}
                                            releaseId={singleRunOverview.releaseId}
                                            stats={singleRunOverview.stats}
                                        />
                                    )}
                                    {shouldShowTimelineWidget && (
                                        <ReleaseTimelineWidget
                                            appId={appIdParam}
                                            orgId={orgIdParam}
                                            releaseId={singleRunOverview.releaseId}
                                            events={singleRunOverview.events}
                                            releaseType={singleRunOverview.type}
                                            maxHeight={290}
                                        />
                                    )}
                                    {shouldShowRolloutsWidgets && (
                                        <WorkflowRunRolloutWidgetsContainer
                                            appId={appId}
                                            orgIdParam={orgIdParam}
                                            appIdParam={appIdParam}
                                            releaseIdParam={singleRunOverview.releaseId}
                                            phasedGroups={singleRunOverview.phasedReleaseWidgetGroups}
                                            appStoreBuildsByDestStepConfigId={
                                                singleRunOverview.selectedAppStoreBuildsByDestinationStepId
                                            }
                                            rolloutMonitoringByDestStepConfigId={
                                                singleRunOverview.rolloutMonitoringByDestinationStepId
                                            }
                                            allDestinations={resolvedDestinations}
                                            selectedDestinationStepConfigIds={selectedDestinationIds}
                                            stabilityIntegrations={stabilityIntegrations}
                                            workflowEnabledSteps={workflowEnabledSteps}
                                            archetypeGroupDetailsMap={
                                                singleRunOverview.archetypeGroupIdToGroupDetailsMap
                                            }
                                        />
                                    )}
                                </>
                            }
                            sideComponent={
                                <>
                                    <WorkflowRunDatesWidgetProps
                                        appId={appId}
                                        selectedDestinations={selectedDestinations}
                                        workflowRunStatus={singleRunOverview.status}
                                        workflowRunTimelinePhase={singleRunOverview.timelinePhase}
                                        workflowsEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                                        targetKickoffDate={singleRunOverview.targetKickoffDate}
                                        targetSubmitDate={singleRunOverview.targetSubmitDate}
                                        targetReleaseDate={singleRunOverview.targetReleaseDate}
                                        skippedAt={singleRunOverview.skippedAt}
                                        kickedOffAt={singleRunOverview.kickedOffAt}
                                        submittedAtByDestinationStepId={
                                            singleRunOverview.submittedAtByDestinationStepId
                                        }
                                        releasedAtByDestinationStepId={singleRunOverview.releasedAtByDestinationStepId}
                                        legacyLifecycleAutoRuns={
                                            singleRunOverview.lifecycleAutomations?.legacyLifecycleAutomations
                                        }
                                        workflowAutoRunsByDestination={
                                            singleRunOverview.lifecycleAutomations
                                                ?.workflowLifecycleAutomationsByDestination
                                        }
                                    />
                                    <FeatureCompletionWidget featuresStep={singleRunOverview.featuresStep} />
                                    <Divider
                                        style={{
                                            margin: '16px 0',
                                        }}
                                    />
                                    <WorkflowRunStepsGraphWidget
                                        runGraph={singleRunOverview.workflowRunGraph}
                                        workflowEnabledStepArchetypes={workflowEnabledSteps}
                                        archetypeGroupDetailsMap={singleRunOverview.archetypeGroupIdToGroupDetailsMap}
                                        stepRunIdToDestinationStepConfigIdsMap={
                                            singleRunOverview.stepRunIdToDestinationStepConfigIdsMap
                                        }
                                        shouldHideNonBlockingArchetypeGroups={shouldHideNonBlockingSteps}
                                        nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                                        allDestinations={resolvedDestinations}
                                        selectedDestinationStepIds={selectedDestinationIds}
                                        releaseType={singleRunOverview.type}
                                        orgIdParam={orgIdParam}
                                        appIdParam={appIdParam}
                                        releaseIdParam={singleRunOverview.releaseId}
                                    />
                                    <Divider
                                        style={{
                                            margin: '16px 0',
                                        }}
                                    />
                                    <ReleasePilotWidget
                                        appId={appId}
                                        releasePilotId={singleRunOverview.releasePilotId}
                                        onReleaseOverview={false}
                                    />
                                </>
                            }
                            headerBackgroundColor={headerTintColor ? colorWithAlpha(headerTintColor, 0.05) : undefined}
                        />
                    );
                })}
            {shouldShowOverviewContent && workflowOverview.workflowRuns.length === 0 && (
                <Flex align="center" justify="center" style={{ height: '100%' }}>
                    <Empty description="No releases" />
                </Flex>
            )}
            {isLoading && (
                <Flex vertical align="center" justify="center" gap={4} style={{ height: '100%' }}>
                    <Spin />
                    <Text
                        style={{
                            color: runwayColors.primary,
                        }}
                    >
                        Loading overview...
                    </Text>
                </Flex>
            )}
            {shouldDisplayFetchFailed && (
                <Result
                    icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                    subTitle="Error fetching overview"
                    extra={<Button onClick={onRetry}>Try again</Button>}
                />
            )}
        </>
    );
};

export default WorkflowOverview;
